import React, { useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import { useRoleSysProvider } from '../../../../../role/hook/role-sys'
import { IconAdd, IconDown, IconSignInMethod, IconUp, IconView } from '../../../../../shared/icon/icon'
import { FleetPage, Notice } from '../../../../../shared/sso-workspace/fleet-page'
import { FleetGrid, useGridList } from '../../../../../shared/sso-workspace/fleet-grid'
import { TableAgClient } from '../../../../../shared/table-builder/table-ag-client'
import { ROW_HEIGHT_TWO_LINE } from '../../../../../shared/table-builder/table-ag'
import { ColumnMenu, ColumnSelector } from '../../../../../shared/table-builder/table-columns'
import { Menu, MenuItem } from '../../../../../shared/menu/menu'
import { Pill } from '../../../../../shared/sso-workspace/pill'
import { ProviderLogo } from '../../../../../shared/sso-workspace/provider-logo'
import { serverMessage } from '../../../../../shared/sso-workspace/format'
import { ErrorReference } from '../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../shared/error/use-error-line'
import { listSigninMethods, orderSigninMethods, setUpSigninMethod } from '../signin-method-api'
import {
  buttonText, exactTime, failureReasonText, lastFailureText, lastSignInText, migrationPendingText, signInCountText, stateView
} from '../signin-method-words'
import { SECTION } from '../../../../../shared/navigation/sections'

export const methodPath = kind => `/admin/signin-methods/${encodeURIComponent(kind)}`

// Sign-in methods (design edxr-workspace/.superpowers/signin-methods §5):
// the buttons every sign-in page offers whatever the person's email --
// Microsoft, Google, Apple, Facebook and whatever auth's registry adds --
// one row per kind, never a connection. Each row: the mark and label, the
// state (On / Temporarily unavailable / Off / Not set up), the order (up and
// down: PUT /signin-method/order), and health (the last sign-in, the last
// failure). A kind with no method yet offers Set up (it starts off, last).
//
// Until edxr-cli's signin_methods migration has run here, auth answers
// available: false: the page says so, naming the migration, and lists the
// kinds read only.
//
// An organization's connection that nobody is sent to is the Connections
// page's "Not in use" filter now, not a section here.
export const SigninMethodList = () => {
  const roleSysProvider = useRoleSysProvider()
  const canEdit = Boolean(roleSysProvider.accessUpdate)

  // undefined while loading, null when the load failed, else auth's answer.
  const [answer, setAnswer] = useState(undefined)
  const [status, setStatus] = useState('')
  const [error, failure, setError] = useErrorLine()
  const [busy, setBusy] = useState(false)
  const tableRef = useRef()

  const load = async () => {
    try {
      setAnswer((await listSigninMethods()) || { available: true, methods: [] })
    } catch (err) {
      setAnswer(null)
      setError(serverMessage(err, "The sign-in methods couldn't be loaded. Reload to try again."), err)
    }
  }

  const available = Boolean(answer && answer.available !== false)
  const methods = useMemo(() => (answer && Array.isArray(answer.methods) ? answer.methods : []), [answer])
  const setUpKinds = useMemo(() => methods.filter(m => m.setUp).map(m => m.kind), [methods])
  const now = Date.now()

  // Auth lists the set-up methods in their order, then the kinds not set
  // up; `order` keeps that order in the grid (its sort). The cells' words are
  // flattened onto the row, so the grid's quick filter and export read what
  // it shows.
  const rows = useMemo(() => {
    if (answer === undefined) return undefined
    if (answer === null) return null
    return methods.map((m, i) => {
      const state = stateView(m)
      const place = m.setUp ? setUpKinds.indexOf(m.kind) : -1
      return {
        ...m,
        id: m.kind,
        order: i,
        place,
        orderText: place >= 0 ? String(place + 1) : '',
        stateText: state.label,
        stateTone: state.tone,
        lastSignIn: lastSignInText(m.health, now),
        signInCount: signInCountText(m.health),
        lastFailure: lastFailureText(m.health, now),
        failureReason: m.health && m.health.lastFailureAt ? failureReasonText(m.health.lastFailureReason, m.label) : ''
      }
    })
  }, [answer, methods, setUpKinds]) // eslint-disable-line react-hooks/exhaustive-deps
  const gridList = useGridList(rows)

  const move = async (row, delta) => {
    const kinds = [...setUpKinds]
    const from = kinds.indexOf(row.kind)
    const to = from + delta
    if (from < 0 || to < 0 || to >= kinds.length) return
    kinds.splice(to, 0, kinds.splice(from, 1)[0])
    setBusy(true)
    setError(null)
    setStatus('')
    try {
      setAnswer(await orderSigninMethods(kinds))
      setStatus(`${row.label} moved ${delta < 0 ? 'up' : 'down'}. Sign-in pages show the buttons in this order.`)
    } catch (err) {
      setError(serverMessage(err, `${row.label} couldn't be moved.`), err)
    } finally {
      setBusy(false)
    }
  }

  const setUp = async row => {
    setBusy(true)
    setError(null)
    setStatus('')
    try {
      await setUpSigninMethod(row.kind)
      setStatus(`${row.label} is set up and off. Open it to check its configuration and switch it on.`)
      await load()
    } catch (err) {
      setError(serverMessage(err, `${row.label} couldn't be set up.`), err)
    } finally {
      setBusy(false)
    }
  }

  const ordering = canEdit && available && !busy

  return (
    <BreadcrumbView Icon={SECTION.signInMethods.Icon} text={SECTION.signInMethods.text}>
      <FleetPage
        tabs
        title="Sign-in methods"
        purpose="The sign-in buttons on every sign-in page, whatever the person's email, in this order. An organization's own connection isn't one of these: people reach it through their email domain.">
        {answer && answer.available === false &&
          <Notice tone="warn" role="status">
            <p className="m-0" data-testid="migration-pending">{migrationPendingText(answer.migration)}</p>
          </Notice>}
        {status && <p className="sw-status text-start mb-3" role="status">{status}</p>}
        {error && <p className="text-danger small" role="alert">{error}</p>}
        {error && <ErrorReference error={failure} />}

        <FleetGrid title="Sign-in methods" tableRef={tableRef} exportName="sign-in-methods">
          <TableAgClient
            ref={tableRef}
            apiList={gridList}
            onReady={load}
            sortCol="order"
            rowHeight={ROW_HEIGHT_TWO_LINE}
            emptyState={{
              Icon: IconSignInMethod,
              title: 'No sign-in methods',
              message: 'This auth service offers no sign-in methods.'
            }}>
            <ColumnSelector field="order" header="Order" selector={row => <OrderCell row={row} count={setUpKinds.length} enabled={ordering} onMove={move} />} />
            <ColumnSelector field="label" header="Sign-in method" selector={row => <MethodCell row={row} linked={available} />} stacked />
            <ColumnSelector field="stateText" header="State" selector={row => <Pill tone={row.stateTone}>{row.stateText}</Pill>} />
            <ColumnSelector field="lastSignIn" header="Last sign-in" selector={row => <SignInCell row={row} />} stacked />
            <ColumnSelector field="lastFailure" header="Last failure" selector={row => <FailureCell row={row} />} stacked />
            <ColumnMenu
              menu={row =>
                <Menu small>
                  <MenuItem Icon={IconView} text="open" to={methodPath(row.kind)} pin show={available} />
                  <MenuItem Icon={IconAdd} text="set up" pin show={available && canEdit && !row.setUp} disabled={busy} onClick={() => setUp(row)} />
                </Menu>}
            />
          </TableAgClient>
        </FleetGrid>

        <Notice>
          <p className="m-0">
            In an incident, make a method <strong>Temporarily unavailable</strong>: its button stays on the sign-in pages, greyed out with your notice, and nobody can sign in with it.
            Off takes the button away. Microsoft's app gallery listing needs "Sign in with Microsoft" to stay visible, so switching Microsoft off asks first.
          </p>
        </Notice>
      </FleetPage>
    </BreadcrumbView>
  )
}

// Its place among the set-up methods, and up and down. A kind not set up
// has no place.
const OrderCell = ({ row, count, enabled, onMove }) => {
  if (row.place < 0) return <span className="sw-hint">—</span>
  return (
    <span className="d-inline-flex align-items-center gap-1">
      <span className="fw-semibold" style={{ minWidth: '1.25rem' }}>{row.orderText}</span>
      {enabled &&
        <>
          <button type="button" className="btn btn-sm sw-btn px-2 py-0" aria-label={`Move ${row.label} up`} title="Move up"
            disabled={row.place === 0} onClick={() => onMove(row, -1)}><IconUp /></button>
          <button type="button" className="btn btn-sm sw-btn px-2 py-0" aria-label={`Move ${row.label} down`} title="Move down"
            disabled={row.place === count - 1} onClick={() => onMove(row, 1)}><IconDown /></button>
        </>}
    </span>
  )
}

// The mark, the label (its screen), and what people see.
const MethodCell = ({ row, linked }) =>
  <span className="d-inline-flex align-items-center gap-2">
    <span className="d-none d-sm-inline-flex"><ProviderLogo iconSVG={row.iconSVG} iconSVGDark={row.iconSVGDark} /></span>
    <span className="min-w-0">
      {linked ? <Link to={methodPath(row.kind)} className="fw-semibold">{row.label}</Link> : <span className="fw-semibold">{row.label}</span>}
      <span className="sw-cell-sub">{buttonText(row.label)}</span>
    </span>
  </span>

const SignInCell = ({ row }) => {
  if (!row.setUp) return <span className="sw-hint">—</span>
  return (
    <span className="min-w-0">
      <span title={exactTime(row.health && row.health.lastSignInAt)}>{row.lastSignIn}</span>
      {row.signInCount && <span className="sw-cell-sub">{row.signInCount}</span>}
    </span>
  )
}

const FailureCell = ({ row }) => {
  if (!row.setUp) return <span className="sw-hint">—</span>
  const failed = Boolean(row.health && row.health.lastFailureAt)
  return (
    <span className="min-w-0">
      <span className={failed ? 'sw-tone-bad fw-semibold' : 'sw-hint'} title={exactTime(failed && row.health.lastFailureAt)}>{row.lastFailure}</span>
      {failed && <span className="sw-cell-sub" title={row.failureReason}>{row.failureReason}</span>}
    </span>
  )
}
